"use client"

import * as React from "react"
import { Search, MessageCircle, Phone, ArrowRight, X } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import { FAQ_DATA, FAQ_CATEGORIES, FAQItem } from "@/lib/faq-data"
import Link from "next/link"

export default function SSSPage() {
    const [searchQuery, setSearchQuery] = React.useState("")
    const [activeCategory, setActiveCategory] = React.useState("Tümü")
    const [openItem, setOpenItem] = React.useState<string | undefined>(undefined)

    const filteredFaqs = React.useMemo(() => {
        return FAQ_DATA.filter((faq) => {
            const matchesCategory = activeCategory === "Tümü" || faq.category === activeCategory
            const matchesSearch =
                faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
            return matchesCategory && matchesSearch
        })
    }, [searchQuery, activeCategory])

    return (
        <div className="min-h-screen bg-muted/30 pb-24">
            {/* Header / Hero Section */}
            <div className="bg-primary text-primary-foreground py-16 md:py-24">
                <div className="container px-4 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl font-bold mb-6"
                    >
                        Sıkça Sorulan Sorular
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-10"
                    >
                        Vize süreçleri, evrak hazırlığı ve merak ettiğiniz tüm soruların cevaplarını burada bulabilirsiniz.
                    </motion.p>

                    {/* Search Bar */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="relative max-w-2xl mx-auto"
                    >
                        <div className="relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                            <Input
                                type="text"
                                placeholder="Soru ara... örn: evrak listesi"
                                className="h-14 pl-12 pr-12 rounded-2xl border-none shadow-xl text-lg text-foreground focus-visible:ring-primary/20 bg-white"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-colors text-muted-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Category Filters */}
            <div className="sticky top-20 z-40 bg-white/80 backdrop-blur-md border-b mb-8 shadow-sm">
                <div className="container px-4 overflow-x-auto no-scrollbar py-4 scroll-smooth">
                    <div className="flex items-center gap-2 min-w-max pb-1">
                        {FAQ_CATEGORIES.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={cn(
                                    "px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap",
                                    activeCategory === category
                                        ? "bg-primary text-white shadow-md shadow-primary/20 scale-105"
                                        : "bg-white text-muted-foreground border hover:border-primary hover:text-primary hover:bg-primary/5"
                                )}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* FAQ List */}
            <div className="container px-4 max-w-4xl mx-auto">
                <AnimatePresence mode="wait">
                    {filteredFaqs.length > 0 ? (
                        <motion.div
                            key={activeCategory + searchQuery}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Accordion
                                type="single"
                                collapsible
                                className="space-y-4"
                                value={openItem}
                                onValueChange={setOpenItem}
                            >
                                {filteredFaqs.map((faq) => (
                                    <AccordionItem
                                        key={faq.id}
                                        value={faq.id}
                                        className={cn(
                                            "bg-white border rounded-2xl overflow-hidden transition-all duration-300 px-2",
                                            openItem === faq.id
                                                ? "border-primary shadow-lg ring-1 ring-primary/20"
                                                : "hover:border-primary/40 hover:shadow-md"
                                        )}
                                    >
                                        <AccordionTrigger className={cn(
                                            "px-4 py-5 hover:no-underline font-bold text-left text-lg transition-colors group",
                                            openItem === faq.id ? "text-primary" : "text-foreground hover:text-primary"
                                        )}>
                                            <span className="flex items-start gap-3">
                                                <span className={cn(
                                                    "w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-black transition-colors",
                                                    openItem === faq.id ? "bg-primary text-white" : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                                                )}>
                                                    Q
                                                </span>
                                                {faq.question}
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-6 pb-6 pt-2">
                                            <div className="flex gap-3 text-muted-foreground leading-relaxed text-base border-t pt-4">
                                                <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center shrink-0 text-secondary font-black text-sm">
                                                    A
                                                </div>
                                                <div>
                                                    {faq.answer}
                                                    <div className="mt-4 flex flex-wrap gap-2 text-xs">
                                                        <span className="bg-muted px-2 py-1 rounded-md font-medium uppercase tracking-wider text-[10px]">
                                                            {faq.category}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-20 bg-white rounded-3xl border border-dashed border-muted-foreground/30"
                        >
                            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                            <h3 className="text-xl font-bold mb-2">Sonuç bulunamadı</h3>
                            <p className="text-muted-foreground">
                                "{searchQuery}" araması için uygun bir soru bulamadık. Lütfen farklı kelimeler deneyin.
                            </p>
                            <Button
                                variant="outline"
                                className="mt-6"
                                onClick={() => {
                                    setSearchQuery("")
                                    setActiveCategory("Tümü")
                                }}
                            >
                                Tümünü Göster
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* CTA Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-20 p-8 md:p-12 rounded-[2.5rem] bg-gradient-to-br from-primary to-primary/90 text-primary-foreground text-center relative overflow-hidden shadow-2xl"
                >
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full -ml-32 -mb-32 blur-3xl pointer-events-none" />

                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Sorunuzun cevabını bulamadınız mı?</h2>
                    <p className="text-primary-foreground/80 text-lg mb-10 max-w-xl mx-auto">
                        Uzman ekibimiz size yardımcı olmaya hazır. Ücretsiz ön değerlendirme için hemen iletişime geçin.
                    </p>

                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Button
                            asChild
                            size="lg"
                            className="bg-[#25D366] hover:bg-[#20ba5a] text-white border-none h-14 px-8 text-lg font-bold rounded-2xl shadow-lg ring-4 ring-white/10"
                        >
                            <Link href="https://wa.me/905331391444" target="_blank" className="flex items-center gap-2">
                                <MessageCircle className="h-6 w-6" />
                                WhatsApp'tan Yazın
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            size="lg"
                            className="bg-white/10 border-white/20 hover:bg-white/20 text-white h-14 px-8 text-lg font-bold rounded-2xl backdrop-blur-sm"
                        >
                            <Link href="tel:+905331391444" className="flex items-center gap-2">
                                <Phone className="h-6 w-6" />
                                Bizi Arayın
                            </Link>
                        </Button>
                    </div>
                </motion.div>
            </div>

            {/* Back to Top / Help Link */}
            <div className="mt-16 text-center">
                <Link href="/" className="text-muted-foreground hover:text-primary flex items-center justify-center gap-1 font-medium transition-colors">
                    Sıkça sorulan soruların sonuna ulaştınız. Anasayfaya dön <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    )
}
